import Period from '../models/Period';
import AcademicYearService, { type AcademicYearStructure } from './AcademicYearService';
import logger from '../utils/logger';
import { getMonthsForAcademicYear, getPreciseWeekRangeForMonth } from '../utils/monthToWeekMapping';
import { parseWeekSeries } from '../utils/timeUtils';
import { enumerateWeeksInRange } from '../utils/schedulePeriodResolver';

type MonthOption = {
    key?: string;
    name: string;
    weekRange: {
        start: number;
        end: number;
    };
    weekSeries: string;
    calendarYear: number;
    type?: string;
};

export type WeekCoordinate = {
    week: number;
    year: number;
};

export type ClassSchedulerTimeFrameSelection = {
    selectedWeek?: { week: number; year?: number } | null;
    selectedMonth?: {
        weekRange?: { start: number; end: number };
        calendarYear?: number;
    } | null;
    selectedPeriodId?: string | number | null;
    timelineViewRange?: {
        startWeek?: number;
        startWeekYear?: number | null;
        endWeek?: number;
        endWeekYear?: number | null;
    } | null;
};

const MONTH_NAMES = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
];

const fallbackCalendarYearFromStructureLabel = (academicYear?: string): number | undefined => {
    const match = String(academicYear || '').match(/^(\d{4})/);
    return match?.[1] ? Number(match[1]) : undefined;
};

const getPeriodWeek = (period: Period | null | undefined, key: 'start_week' | 'end_week'): number | null => {
    const raw = period?.[key];
    const value = Number(raw);
    return Number.isFinite(value) ? value : null;
};

const getPeriodYear = (period: Period | null | undefined, key: 'start_week_year' | 'end_week_year' | 'year'): number | null => {
    const raw = period?.[key];
    const value = Number(raw);
    return Number.isFinite(value) ? value : null;
};

const inferPeriodEndYear = (startWeek: number | null, endWeek: number | null, startYear: number | null): number | null => {
    if (startWeek == null || endWeek == null || startYear == null) {
        return null;
    }

    return endWeek < startWeek ? startYear + 1 : startYear;
};

const normalizeStartOfDay = (date: Date): Date => new Date(date.getFullYear(), date.getMonth(), date.getDate());

const normalizeEndOfDay = (date: Date): Date => new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);

const isValidDate = (date: Date): boolean => date instanceof Date && !Number.isNaN(date.getTime());

const weekCoordinateKey = ({ week, year }: WeekCoordinate): string => `${week}-${year}`;

const buildWeekRange = (startWeek: number, endWeek: number, year: number): WeekCoordinate[] => {
    if (startWeek <= endWeek) {
        return Array.from({ length: endWeek - startWeek + 1 }, (_, index) => ({
            week: startWeek + index,
            year
        }));
    }

    return [
        ...Array.from({ length: 53 - startWeek + 1 }, (_, index) => ({
            week: startWeek + index,
            year
        })),
        ...Array.from({ length: endWeek }, (_, index) => ({
            week: index + 1,
            year: year + 1
        }))
    ];
};

const getCalendarMonthsForYear = (year: number): MonthOption[] => (
    MONTH_NAMES.map((name) => {
        const preciseRange = getPreciseWeekRangeForMonth(name, year) as any;
        const weekRange = {
            start: Number(preciseRange.start),
            end: Number(preciseRange.end)
        };
        const weekSeries = weekRange.start === weekRange.end
            ? String(weekRange.start)
            : `${weekRange.start}-${weekRange.end}`;

        return {
            name,
            weekRange,
            weekSeries,
            calendarYear: year,
            type: 'month'
        };
    })
);

class PeriodManager {
    private static instance: PeriodManager;
    private academicYearService: AcademicYearService;
    private periods: Period[] = [];

    private constructor() {
        this.academicYearService = AcademicYearService.getInstance();
    }

    static getInstance(): PeriodManager {
        if (!PeriodManager.instance) {
            PeriodManager.instance = new PeriodManager();
        }

        return PeriodManager.instance;
    }

    static getMonthKey(month: Pick<MonthOption, 'name' | 'calendarYear'>): string {
        return `${month.name}-${month.calendarYear}`;
    }

    static fromYearStructure(structure: AcademicYearStructure | null | undefined): Period[] {
        const periods = structure?.periods;
        if (!Array.isArray(periods)) {
            return [];
        }

        return periods.map(periodData => Period.fromApiData({
            ...periodData,
            year: periodData?.year
                ?? periodData?.start_week_year
                ?? periodData?.startWeekYear
                ?? fallbackCalendarYearFromStructureLabel(structure?.academicYear)
        }));
    }

    static getPeriodStartYear(period: Period | null | undefined, academicYearStructure?: AcademicYearStructure | null): number | null {
        return getPeriodYear(period, 'start_week_year')
            ?? getPeriodYear(period, 'year')
            ?? fallbackCalendarYearFromStructureLabel(academicYearStructure?.academicYear)
            ?? null;
    }

    static getPeriodEndYear(period: Period | null | undefined, academicYearStructure?: AcademicYearStructure | null): number | null {
        const explicitEndYear = getPeriodYear(period, 'end_week_year');
        if (explicitEndYear != null) {
            return explicitEndYear;
        }

        const startWeek = getPeriodWeek(period, 'start_week');
        const endWeek = getPeriodWeek(period, 'end_week');
        return inferPeriodEndYear(startWeek, endWeek, PeriodManager.getPeriodStartYear(period, academicYearStructure));
    }

    static getWeeksForPeriod(period: Period | null | undefined, academicYearStructure?: AcademicYearStructure | null): Array<{ week: number; year: number }> {
        const startWeek = getPeriodWeek(period, 'start_week');
        const endWeek = getPeriodWeek(period, 'end_week');
        const startYear = PeriodManager.getPeriodStartYear(period, academicYearStructure);

        if (startWeek == null || endWeek == null || startYear == null) {
            return [];
        }

        const endYear = PeriodManager.getPeriodEndYear(period, academicYearStructure) ?? startYear;
        return PeriodManager.getWeeksForWeekRange(startWeek, startYear, endWeek, endYear);
    }

    static getPeriodsOverlappingDateRange(periods: Period[], rangeStart: Date, rangeEnd: Date): Period[] {
        if (!Array.isArray(periods) || !isValidDate(rangeStart) || !isValidDate(rangeEnd)) {
            return [];
        }

        const normalizedStart = normalizeStartOfDay(rangeStart);
        const normalizedEnd = normalizeEndOfDay(rangeEnd);
        if (normalizedStart > normalizedEnd) {
            return [];
        }

        return periods
            .filter(Boolean)
            .filter((period) => {
                const periodStart = period.getStartDate();
                const periodEnd = period.getEndDate();
                if (!isValidDate(periodStart) || !isValidDate(periodEnd)) {
                    return false;
                }

                return periodStart <= normalizedEnd && periodEnd >= normalizedStart;
            })
            .sort((a, b) => a.getStartDate().getTime() - b.getStartDate().getTime());
    }

    static getMonthsCoveringPeriods(periods: Period[], academicYearStructure?: AcademicYearStructure | null): MonthOption[] {
        const validPeriods = Array.isArray(periods) ? periods.filter(Boolean) : [];
        if (validPeriods.length === 0) {
            return getMonthsForAcademicYear((academicYearStructure ?? null) as any).map((month: MonthOption) => ({
                ...month,
                key: PeriodManager.getMonthKey(month)
            }));
        }

        const years = new Set<number>();
        validPeriods.forEach((period) => {
            const startYear = PeriodManager.getPeriodStartYear(period, academicYearStructure);
            const endYear = PeriodManager.getPeriodEndYear(period, academicYearStructure);
            if (startYear != null) years.add(startYear);
            if (endYear != null) years.add(endYear);
        });

        if (years.size === 0) {
            const fallbackYear = fallbackCalendarYearFromStructureLabel(academicYearStructure?.academicYear) ?? new Date().getFullYear();
            years.add(fallbackYear);
        }

        const periodWeeks = validPeriods.reduce<Array<{ week: number; year: number }>>((acc, period) => (
            acc.concat(PeriodManager.getWeeksForPeriod(period, academicYearStructure))
        ), []);
        const periodWeekKeys = new Set(periodWeeks.map(({ week, year }) => `${week}-${year}`));
        const seenMonths = new Set<string>();

        return Array.from(years)
            .sort((a, b) => a - b)
            .reduce<MonthOption[]>((acc, year) => acc.concat(getCalendarMonthsForYear(year)), [])
            .map((month: MonthOption) => ({
                ...month,
                key: PeriodManager.getMonthKey(month)
            }))
            .filter((month: MonthOption) => {
                if (seenMonths.has(month.key || '')) {
                    return false;
                }
                seenMonths.add(month.key || '');

                return buildWeekRange(month.weekRange.start, month.weekRange.end, month.calendarYear)
                    .some(({ week, year }) => periodWeekKeys.has(`${week}-${year}`));
            });
    }

    static getMonthsForPeriod(period: Period | null | undefined, months: MonthOption[], academicYearStructure?: AcademicYearStructure | null): MonthOption[] {
        const periodWeeks = new Set(
            PeriodManager.getWeeksForPeriod(period, academicYearStructure)
                .map(({ week, year }) => `${week}-${year}`)
        );

        if (periodWeeks.size === 0) {
            return [];
        }

        return months.filter(month => buildWeekRange(month.weekRange.start, month.weekRange.end, month.calendarYear)
            .some(({ week, year }) => periodWeeks.has(`${week}-${year}`)));
    }

    static getFirstMonthForPeriod(period: Period | null | undefined, months: MonthOption[], academicYearStructure?: AcademicYearStructure | null): MonthOption | null {
        return PeriodManager.getMonthsForPeriod(period, months, academicYearStructure)[0] ?? null;
    }

    static getWeeksForClassSchedulerTimeFrame(
        selection: ClassSchedulerTimeFrameSelection,
        periods: Period[],
        academicYearStructure?: AcademicYearStructure | null
    ): WeekCoordinate[] {
        const { selectedWeek, selectedMonth, selectedPeriodId, timelineViewRange } = selection;
        const safePeriods = Array.isArray(periods) ? periods.filter(Boolean) : [];

        if (
            timelineViewRange
            && typeof timelineViewRange.startWeek === 'number'
            && typeof timelineViewRange.endWeek === 'number'
        ) {
            const anchorPeriod = safePeriods.find((period) => String(period.id) === String(selectedPeriodId));
            const startYear = Number(timelineViewRange.startWeekYear)
                || PeriodManager.getPeriodStartYear(anchorPeriod, academicYearStructure)
                || fallbackCalendarYearFromStructureLabel(academicYearStructure?.academicYear)
                || new Date().getFullYear();
            const endYear = Number(timelineViewRange.endWeekYear)
                || PeriodManager.getPeriodEndYear(anchorPeriod, academicYearStructure)
                || inferPeriodEndYear(timelineViewRange.startWeek, timelineViewRange.endWeek, startYear)
                || startYear;

            return PeriodManager.getWeeksForWeekRange(
                Number(timelineViewRange.startWeek),
                startYear,
                Number(timelineViewRange.endWeek),
                endYear
            );
        }

        if (selectedWeek && typeof selectedWeek.week === 'number') {
            const anchorPeriod = safePeriods.find((period) => String(period.id) === String(selectedPeriodId));
            const year = selectedWeek.year
                ?? PeriodManager.getPeriodStartYear(anchorPeriod, academicYearStructure)
                ?? fallbackCalendarYearFromStructureLabel(academicYearStructure?.academicYear)
                ?? new Date().getFullYear();

            return [{ week: selectedWeek.week, year }];
        }

        if (
            selectedMonth?.weekRange
            && typeof selectedMonth.weekRange.start === 'number'
            && typeof selectedMonth.weekRange.end === 'number'
        ) {
            const startYear = selectedMonth.calendarYear
                ?? fallbackCalendarYearFromStructureLabel(academicYearStructure?.academicYear)
                ?? new Date().getFullYear();

            return buildWeekRange(
                selectedMonth.weekRange.start,
                selectedMonth.weekRange.end,
                startYear
            );
        }

        const selectedPeriod = safePeriods.find((period) => String(period.id) === String(selectedPeriodId));
        if (!selectedPeriod) {
            return [];
        }

        return PeriodManager.getWeeksForPeriod(selectedPeriod, academicYearStructure);
    }

    static getWeeksForWeekRange(
        startWeek: number,
        startYear: number,
        endWeek: number,
        endYear: number
    ): WeekCoordinate[] {
        return enumerateWeeksInRange(startWeek, startYear, endWeek, endYear);
    }

    static getPeriodsOverlappingWeekRange(
        periods: Period[],
        startWeek: number,
        startYear: number,
        endWeek: number,
        endYear: number,
        academicYearStructure?: AcademicYearStructure | null
    ): Period[] {
        const safePeriods = Array.isArray(periods) ? periods.filter(Boolean) : [];
        const rangeWeeks = PeriodManager.getWeeksForWeekRange(startWeek, startYear, endWeek, endYear);
        if (rangeWeeks.length === 0 || safePeriods.length === 0) {
            return [];
        }

        const rangeWeekKeys = new Set(rangeWeeks.map(weekCoordinateKey));

        return safePeriods.filter((period) => PeriodManager.getWeeksForPeriod(period, academicYearStructure)
            .some((week) => rangeWeekKeys.has(weekCoordinateKey(week))));
    }

    static getPeriodIdsOverlappingClassSchedulerTimeFrame(
        periods: Period[],
        selection: ClassSchedulerTimeFrameSelection,
        academicYearStructure?: AcademicYearStructure | null
    ): Array<Period['id']> {
        const safePeriods = Array.isArray(periods) ? periods.filter(Boolean) : [];
        const timeFrameWeeks = PeriodManager.getWeeksForClassSchedulerTimeFrame(
            selection,
            safePeriods,
            academicYearStructure
        );

        if (timeFrameWeeks.length === 0 || safePeriods.length === 0) {
            return [];
        }

        const timeFrameWeekKeys = new Set(timeFrameWeeks.map(weekCoordinateKey));

        return safePeriods
            .filter((period) => PeriodManager.getWeeksForPeriod(period, academicYearStructure)
                .some((week) => timeFrameWeekKeys.has(weekCoordinateKey(week))))
            .map((period) => period.id);
    }

    static hasMultiplePeriodsOverlappingClassSchedulerTimeFrame(
        periods: Period[],
        selection: ClassSchedulerTimeFrameSelection,
        academicYearStructure?: AcademicYearStructure | null
    ): boolean {
        return PeriodManager.getPeriodIdsOverlappingClassSchedulerTimeFrame(
            periods,
            selection,
            academicYearStructure
        ).length > 1;
    }

    static getScheduleEntryWeekCoordinates(
        entry: {
            weekSeries?: string;
            year?: number;
            periodId?: string | number | null;
            period_id?: string | number | null;
            getActiveWeeksSync?: (startYear: number, endYear: number) => WeekCoordinate[];
            resolvePeriodScope?: (structure?: AcademicYearStructure | null) => {
                academicYearStart?: number | null;
                startWeekYear?: number | null;
                endWeekYear?: number | null;
                periodRef?: number | null;
            };
        } | null | undefined,
        academicYearStructure?: AcademicYearStructure | null
    ): WeekCoordinate[] {
        if (!entry) {
            return [];
        }

        const resolvedScope = typeof entry.resolvePeriodScope === 'function'
            ? entry.resolvePeriodScope(academicYearStructure ?? undefined)
            : null;
        const fallbackYear = resolvedScope?.academicYearStart
            ?? fallbackCalendarYearFromStructureLabel(academicYearStructure?.academicYear)
            ?? (Number.isFinite(Number(entry.year)) ? Number(entry.year) : new Date().getFullYear());
        const startYear = resolvedScope?.startWeekYear ?? fallbackYear;
        const endYear = resolvedScope?.endWeekYear ?? startYear;

        if (typeof entry.getActiveWeeksSync === 'function' && startYear != null && endYear != null) {
            try {
                const activeWeeks = entry.getActiveWeeksSync(startYear, endYear);
                if (Array.isArray(activeWeeks) && activeWeeks.length > 0) {
                    return activeWeeks
                        .filter((week) => Number.isFinite(Number(week?.week)) && Number.isFinite(Number(week?.year)))
                        .map((week) => ({ week: Number(week.week), year: Number(week.year) }));
                }
            } catch (_) {
                // Fall back to weekSeries parsing below.
            }
        }

        if (!entry.weekSeries) {
            return [];
        }

        const entryWeekNumbers = parseWeekSeries(entry.weekSeries);
        if (!entryWeekNumbers.length) {
            return [];
        }

        return entryWeekNumbers.map((week) => ({ week, year: fallbackYear }));
    }

    static scheduleEntryBelongsToPeriod(
        entry: {
            periodId?: string | number | null;
            period_id?: string | number | null;
            weekSeries?: string;
            year?: number;
            getActiveWeeksSync?: (startYear: number, endYear: number) => WeekCoordinate[];
            resolvePeriodScope?: (structure?: AcademicYearStructure | null) => {
                academicYearStart?: number | null;
                startWeekYear?: number | null;
                endWeekYear?: number | null;
                periodRef?: number | null;
            };
        } | null | undefined,
        period: Period | null | undefined,
        academicYearStructure?: AcademicYearStructure | null
    ): boolean {
        if (!entry || !period) {
            return false;
        }

        const rawPeriodId = entry.periodId ?? entry.period_id;
        if (rawPeriodId != null && rawPeriodId !== '') {
            return String(rawPeriodId) === String(period.id);
        }

        if (typeof entry.resolvePeriodScope === 'function') {
            const scope = entry.resolvePeriodScope(academicYearStructure ?? undefined);
            if (scope?.periodRef != null && String(scope.periodRef) === String(period.id)) {
                return true;
            }
        }

        const periodWeekKeys = new Set(
            PeriodManager.getWeeksForPeriod(period, academicYearStructure).map(weekCoordinateKey)
        );
        if (periodWeekKeys.size === 0) {
            return false;
        }

        return PeriodManager.getScheduleEntryWeekCoordinates(entry, academicYearStructure)
            .some((week) => periodWeekKeys.has(weekCoordinateKey(week)));
    }

    static scheduleEntryBelongsToAnyPeriod(
        entry: Parameters<typeof PeriodManager.scheduleEntryBelongsToPeriod>[0],
        periods: Period[],
        academicYearStructure?: AcademicYearStructure | null
    ): boolean {
        const safePeriods = Array.isArray(periods) ? periods.filter(Boolean) : [];
        return safePeriods.some((period) => PeriodManager.scheduleEntryBelongsToPeriod(
            entry,
            period,
            academicYearStructure
        ));
    }

    setPeriods(periods: Period[]): Period[] {
        this.periods = periods;
        return this.periods;
    }

    getCachedPeriods(): Period[] {
        return this.periods;
    }

    async fetchPeriods(): Promise<Period[]> {
        logger.debug('[PeriodManager] Fetching periods from academic year structure...');
        // Always refetch institution periods — academic-year session cache must not hide newly visible periods.
        this.academicYearService.clearCache();
        const structure = await this.academicYearService.getAcademicYearStructure();
        return this.setPeriods(PeriodManager.fromYearStructure(structure));
    }

    async refreshPeriods(): Promise<Period[]> {
        this.academicYearService.clearCache();
        return this.fetchPeriods();
    }

    getPeriodById(periodId: string | number | null | undefined): Period | null {
        if (periodId == null || periodId === '') {
            return null;
        }

        return this.periods.find(period => String(period.id) === String(periodId)) ?? null;
    }

    async fetchPeriod(periodId: string | number): Promise<Period> {
        const cached = this.getPeriodById(periodId);
        if (cached) {
            return cached;
        }

        const periods = await this.fetchPeriods();
        const period = periods.find(item => String(item.id) === String(periodId));
        if (!period) {
            throw new Error('Period not found in year structure');
        }

        return period;
    }
}

export default PeriodManager;
